'use strict';

// The cardNav directive handles overflow in the client card tab bar.
// It measures available width and hides left-aligned tabs that don't fit,
// exposing them via $scope.AccountCtrl.overflowTabs so a "More" dropdown
// (rendered by the <li class="more-tabs"> element in the template) can
// surface them. Right-aligned action buttons (.pull-right) stay visible.

angular.module('upDirectives').directive('cardNav', function () {
	const MORE_BTN_WIDTH = 50; // Hamburger trigger width reserve
	const SAFETY = 10;

	function extractTabData(li, key) {
		const compactA = li.querySelector('a.compact');
		const regularA = li.querySelector('a.regular');
		const anyA = li.querySelector('a');

		let iconHtml = '';
		const iconSource = compactA || anyA;
		if (iconSource) {
			const iconEl = iconSource.querySelector('b, i, div.Icon, span.Icon');
			if (iconEl) {
				iconHtml = iconEl.outerHTML;
			}
		}

		let label = '';
		const labelSource = regularA || anyA;
		if (labelSource) {
			const labelEl = labelSource.querySelector('span:not(.counter)');
			if (labelEl) {
				label = labelEl.textContent.trim();
			}
		}

		let counter = '';
		const counterSource = compactA || regularA;
		if (counterSource) {
			const counterEl = counterSource.querySelector('.counter');
			if (counterEl) {
				counter = counterEl.textContent.trim();
			}
		}

		return {
			id: key,
			iconHtml: iconHtml,
			label: label,
			counter: counter
		};
	}

	return {
		restrict: 'A',
		link: function ($scope, navElem) {
			let navCopy;

			// Map from generated stable key -> source <li> element. Used by the
			// overflow dropdown to navigate. We can't rely on DOM ids because
			// some tabs (home, UDOs) lack one and others (account-nav-order) are
			// duplicated across different <li>s.
			const itemMap = Object.create(null);

			function cloneElem() {
				const tmp = navElem.clone().removeClass('nav-compact').addClass('clone').removeAttr('id');
				// Ensure all items are visible in the clone for accurate width measurement
				tmp.find('.card-nav').removeClass('tab-overflow-hidden');
				tmp.find('.more-tabs').remove();
				if (navCopy) {
					navCopy.replaceWith(tmp);
				} else {
					tmp.insertAfter(navElem);
				}
				navCopy = tmp;
			}

			// Greedy left-to-right fit. Returns { fitting, overflow } as arrays
			// of { real, clone, width } objects. availableWidth is the budget
			// the left-aligned tabs may occupy.
			function fitTabs(realLeft, cloneLeft, availableWidth) {
				const fitting = [];
				const overflow = [];
				let used = 0;
				realLeft.forEach((realLi, i) => {
					const cloneLi = cloneLeft[i];
					if (!cloneLi) {
						return;
					}
					const width = angular.element(cloneLi).outerWidth();
					if (overflow.length === 0 && used + width <= availableWidth) {
						used += width;
						fitting.push({ real: realLi, width: width });
					} else {
						overflow.push({ real: realLi, width: width });
					}
				});
				return { fitting: fitting, overflow: overflow };
			}

			function calculateOverflow() {
				if (!navCopy) {
					cloneElem();
				}
				if (!navCopy) {
					return;
				}

				const containerWidth = navCopy.innerWidth();
				if (!containerWidth) {
					return;
				}

				const cloneItems = navCopy.find('.card-nav').toArray();
				const realItems = navElem.find('.card-nav').not('.more-tabs').toArray();

				const cloneLeft = cloneItems.filter(li => !li.classList.contains('pull-right'));
				const cloneRight = cloneItems.filter(li => li.classList.contains('pull-right'));
				const realLeft = realItems.filter(li => !li.classList.contains('pull-right'));

				const rightWidth = cloneRight.reduce((sum, li) => sum + angular.element(li).outerWidth(), 0);

				// First pass: try to fit every tab WITHOUT reserving burger space.
				// If they all fit, no burger needed and no tabs are hidden.
				const availableNoBurger = containerWidth - rightWidth - SAFETY;
				let result = fitTabs(realLeft, cloneLeft, availableNoBurger);

				// Second pass: if anything overflowed, redo the fit with the burger
				// width reserved so the visible burger doesn't push later tabs out.
				if (result.overflow.length > 0) {
					const availableWithBurger = containerWidth - rightWidth - MORE_BTN_WIDTH - SAFETY;
					result = fitTabs(realLeft, cloneLeft, availableWithBurger);
				}

				// Clear stale entries before repopulating.
				for (const k in itemMap) {
					delete itemMap[k];
				}

				const overflowData = [];
				result.fitting.forEach(({ real }) => {
					angular.element(real).removeClass('tab-overflow-hidden');
				});
				result.overflow.forEach(({ real }, idx) => {
					angular.element(real).addClass('tab-overflow-hidden');
					const key = 'overflow-' + idx;
					itemMap[key] = real;
					overflowData.push(extractTabData(real, key));
				});

				$scope.$applyAsync(() => {
					if ($scope.AccountCtrl) {
						$scope.AccountCtrl.overflowTabs = overflowData;
					}
				});
			}

			// Expose the click handler via the controller so the React dropdown
			// can trigger the original ui-sref click, keeping all Angular routing
			// intact regardless of whether the source <li> has an id or not.
			if ($scope.AccountCtrl) {
				$scope.AccountCtrl.onOverflowTabSelect = function (key) {
					const li = itemMap[key];
					if (!li) {
						return;
					}
					const link = li.querySelector('a');
					if (link) {
						link.click();
					}
				};
			}

			if (window.ResizeObserver && window.MutationObserver) {
				const domElement = navElem[0];

				// Resize observer fires for the container (viewport change) AND each
				// .card-nav <li>. React-driver components (AlertIcon, SignalsToggle,
				// ClientCardSplitButton, Button, ContactScraper) mount asynchronously
				// inside .pull-right <li>s and change their widths — watching each
				// item keeps our clone's measurements fresh.
				const debouncedRemeasure = _.debounce(() => {
					cloneElem();
					observeAllItems(); // eslint-disable-line no-use-before-define
					calculateOverflow();
				}, 50);

				const resizeObserver = new ResizeObserver(() => {
					window.requestAnimationFrame(debouncedRemeasure);
				});

				const observeAllItems = () => {
					domElement.querySelectorAll('.card-nav').forEach(li => {
						resizeObserver.observe(li);
					});
				};

				resizeObserver.observe(domElement);
				observeAllItems();

				const mutationObserver = new MutationObserver(debouncedRemeasure);
				mutationObserver.observe(domElement, { childList: true });

				// Initial pass
				cloneElem();
				calculateOverflow();

				$scope.$on('$destroy', () => {
					resizeObserver.disconnect();
					mutationObserver.disconnect();
				});
			} else {
				const winElem = angular.element(window);

				const onResize = _.debounce(() => {
					cloneElem();
					calculateOverflow();
				}, 100);

				winElem.on('resize', onResize);

				// Initial pass
				cloneElem();
				calculateOverflow();

				$scope.$on('$destroy', function () {
					winElem.off('resize', onResize);
				});
			}
		}
	};
});
